import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RECEIVE_OPTIONS, ReceiveOption, ReceiveOptionKey } from './receiveOptions';
import { IconBadge } from './ui';

// The payout selector (WU-RECV), now part of "Global reach and delivery" (WU-REACH). The visitor picks an option
// (tabs, keyboard included) and the panel shows it; nothing rotates on its own. Moved here verbatim.
const PANEL_ID = 'receive-panel';
const tabId = (key: ReceiveOptionKey) => `receive-tab-${key}`;

const PayoutSelector: React.FC = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ReceiveOptionKey>('cash');
  // The panel's entrance animation plays only after the visitor changes option, never on load.
  const [changed, setChanged] = useState(false);
  const tabRefs = useRef<Record<ReceiveOptionKey, HTMLButtonElement | null>>({ cash: null, mobile: null, bank: null });

  const choose = (key: ReceiveOptionKey, focus = false) => {
    if (key !== selected) {
      setSelected(key);
      setChanged(true);
    }
    if (focus) tabRefs.current[key]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = RECEIVE_OPTIONS.findIndex((option) => option.key === selected);
    const last = RECEIVE_OPTIONS.length - 1;
    let next: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next == null) return;
    event.preventDefault();
    choose(RECEIVE_OPTIONS[next].key, true);
  };

  const statusShort = (option: ReceiveOption) => t(option.available ? 'receive.statusShort.now' : 'receive.statusShort.soon');

  const panelContent = (option: ReceiveOption) => (
    <>
      <div className="receive-panel__media" aria-hidden="true">
        {option.media ?? <IconBadge name={option.icon} size={56} className="receive-panel__badge" />}
      </div>
      <h3 className="receive-panel__title">{t(`receive.options.${option.key}.title`)}</h3>
      <p className="receive-panel__body">{t(`receive.options.${option.key}.body`)}</p>
      <p className={`receive-panel__status receive-panel__status--${option.available ? 'now' : 'soon'}`}>
        <span className="receive-panel__dot" aria-hidden="true" />
        {t(`receive.options.${option.key}.status`)}
      </p>
    </>
  );

  const current = RECEIVE_OPTIONS.find((option) => option.key === selected) ?? RECEIVE_OPTIONS[0];

  return (
    <div className="receive-selector">
      <div className="receive-options" role="tablist" aria-label={t('receive.tabsLabel')}>
        {RECEIVE_OPTIONS.map((option) => {
          const isSelected = option.key === selected;
          return (
            <button
              key={option.key}
              ref={(element) => {
                tabRefs.current[option.key] = element;
              }}
              type="button"
              role="tab"
              id={tabId(option.key)}
              className="receive-option"
              aria-selected={isSelected}
              aria-controls={PANEL_ID}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => choose(option.key)}
              onKeyDown={onKeyDown}
            >
              <IconBadge name={option.icon} className="receive-option__icon" />
              <span className="receive-option__text">
                <span className="receive-option__label">{t(`receive.options.${option.key}.label`)}</span>
                <span className={`receive-option__status receive-option__status--${option.available ? 'now' : 'soon'}`}>
                  {statusShort(option)}
                </span>
              </span>
              <span className="receive-option__check" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      <div
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={tabId(selected)}
        className={current.available ? 'receive-panel' : 'receive-panel receive-panel--soon'}
      >
        <div className="receive-panel__stack">
          <div key={selected} className={changed ? 'receive-panel__content receive-panel__content--enter' : 'receive-panel__content'}>
            {panelContent(current)}
          </div>
          {/* Invisible copies of every option share the cell, so the panel keeps the tallest height. */}
          {RECEIVE_OPTIONS.map((option) => (
            <div key={option.key} className="receive-panel__content receive-panel__ghost" aria-hidden="true">
              {panelContent(option)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PayoutSelector;
